import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import Tag from '../tag';
import UserCountBadge from '../userCountBadge';
import VirtualizedList from '../virtualizedList';
import User from './user';
import CustomButton from '../customButton';
import { isArrayEmpty } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    borderRadius: 4,
    border: ({ color }) => `1px solid ${color}`,
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(6),
    width: '100%',
    height: 282,
    minHeight: 0,
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing(4),
  },
  userCount: {
    flexShrink: 0,
    marginLeft: spacing(2),
    backgroundColor: 'unset',
    cursor: 'default',
  },
  usersWrapper: {
    minHeight: 0,
    flex: 1,
  },
  userWrapper: {
    borderBottom: `1px solid ${primary.bluish7}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  scrollY: {
    right: -15,
  },
  manageUsersButtonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginBottom: spacing(2),
  },
}));

const UsersTagCard = ({
  tag,
  color,
  manageUsersButtonLabel,
  canAccessUserProfileCheck,
  hasManageUsersButton,
  onGoToUserProfile,
  onManageUsers,
}) => {
  const classes = useStyles({ color });

  const totalUsers = tag.users.length;

  const getVirtualUserSize = useCallback(
    index => (index === totalUsers - 1 ? 24 : 32),
    [totalUsers]
  );

  const renderUser = useCallback(
    user => {
      const isAccessible = canAccessUserProfileCheck(user.id);

      return (
        <User
          user={user}
          isAccessible={isAccessible}
          onGoToProfilePage={onGoToUserProfile}
        />
      );
    },
    [canAccessUserProfileCheck, onGoToUserProfile]
  );

  return (
    <div className={classes.root}>
      <div className={classes.titleWrapper}>
        <Tag tag={tag} color={color} isSelected isSmall />
        <UserCountBadge className={classes.userCount} userCount={totalUsers} />
      </div>
      {hasManageUsersButton && (
        <div className={classes.manageUsersButtonWrapper}>
          <CustomButton
            type="addSmallDarkRoundedOutlined"
            onClick={onManageUsers}
          >
            {manageUsersButtonLabel}
          </CustomButton>
        </div>
      )}
      {!isArrayEmpty(tag.users) && (
        <div className={classes.usersWrapper}>
          <VirtualizedList
            verticalScrollClass={classes.scrollY}
            itemClass={classes.userWrapper}
            items={tag.users}
            count={totalUsers}
            gap={8}
            estimateSize={getVirtualUserSize}
            renderItem={renderUser}
          />
        </div>
      )}
    </div>
  );
};

UsersTagCard.propTypes = {
  manageUsersButtonLabel: PropTypes.string.isRequired,
  tag: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
  hasManageUsersButton: PropTypes.bool.isRequired,
  onManageUsers: PropTypes.func.isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  onGoToUserProfile: PropTypes.func.isRequired,
};

export default UsersTagCard;
